import { lazy, Suspense, useMemo } from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';
import routeUrlProvider, {
  HOME as HOME_PATH,
  HOTEL as HOTEL_PATH,
  PAYMENTS as PAYMENT_PATH,
  AMOUNT_LESS_PAYMENT as AMOUNT_LESS_PAYMENT_PATH,
  QR_PAYMENT as QR_PAYMENT_PATH,
  RESULT as RESULT_PATH,
  QR_RESULT as QR_RESULT_PATH,
  SEND_QR_RECEIPT as SEND_QR_RECEIPT_PATH,
  CAPTURE_LATER_RESULT as CAPTURE_LATER_RESULT_PATH,
  ORACLE_PAYMENT as ORACLE_PAYMENT_PATH,
  ORACLE_PAYMENT_DETAIL as ORACLE_PAYMENT_DETAIL_PATH,
  ERROR_HAS_BEEN_PAID as ERROR_HAS_BEEN_PAID_PATH,
  ERROR_FULL_ACCESS as ERROR_FULL_ACCESS_PATH,
  VERIFY_YOUR_PAYMENT as VERIFY_YOUR_PAYMENT_PATH,
  LINK_EXPIRED as LINK_EXPIRED_PATH,
  PAYMENT_STATUS as PAYMENT_STATUS_PATH,
  SOMETHING_WRONG as SOMETHING_WRONG_PATH,
  RECEIPT as RECEIPT_PATH,
  RECEIPT_WITH_TOKEN as RECEIPT_WITH_TOKEN_PATH,
  SHOP as SHOP_PATH,
  SHOP_SUCCESS as SHOP_SUCCESS_PATH,
  WIDGET_DESIGNER as WIDGET_DESIGNER_PATH,
} from 'constants/route-paths';
import RewardFormContextProvider from 'contexts/rewardForm';
import ContextRoute from 'contexts/route';
import SplitBillRoute from './split-bill';
import ThirdPaymentRoute from './third-payment';
import { PaymentServiceRepository } from 'services/repository/PaymentServiceRepository/payment-service.repository';
import PaymentService from 'services/payment-service';
import FullPageSpinner from 'components/FullPageSpinner';
import ErrorHasBeenPaid from 'components/FullPageErrorFallback/ErrorHasBeenPaid';
import ErrorFullAccess from 'components/FullPageErrorFallback/ErrorFullAccess';
import PaymentEmailVerify from 'components/PaymentEmailVerify';
import LinkHasExpired from 'components/FullPageErrorFallback/LinkHasExpired';
import PaymentStatus from 'components/PaymentStatus';
import ErrorSomethingWrong from 'components/FullPageErrorFallback/ErrorSomethingWrong';

import lazyWithRetry from './lazyWithRetry';

const FullPageErrorFallback = lazy(() => import('components/FullPageErrorFallback'));

const AmountLessPayment = lazy(() => import('../pages/AmountLessPayment'));
const CaptureResult = lazy(() => import('../pages/CaptureResult'));
const Demo = lazy(() => import('../pages/Demo'));
const OraclePayment = lazy(() => import('../pages/OraclePayment'));
const OraclePaymentDetail = lazy(() => import('../pages/OraclePaymentDetail'));
const Payments = lazyWithRetry(() => import('../pages/Payments'));
const QrPayment = lazyWithRetry(() => import('../pages/QrPayment'));
const Receipt = lazy(() => import('../pages/Receipt'));
const ResultPayment = lazy(() => import('../pages/ResultPayment'));
const SendReceipt = lazy(() => import('../pages/SendReceipt'));
const Shop = lazyWithRetry(() => import('../pages/Shop'));
const ShopSuccess = lazy(() => import('../pages/Shop/Success'));
const WidgetDesigner = lazy(() => import('../pages/WidgetDesigner'));

const Router = () => {
  const paymentServiceRepository = useMemo(() => {
    return new PaymentServiceRepository(new PaymentService());
  }, []);

  return (
    <Switch>
      {ThirdPaymentRoute}
      {SplitBillRoute}

      <Route
        exact
        path={[routeUrlProvider.getForRoute(HOME_PATH), routeUrlProvider.getForRoute(HOTEL_PATH)]}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <Demo {...props} />
          </Suspense>
        )}
      />

      <ContextRoute
        exact
        path={routeUrlProvider.getForRoute(RESULT_PATH)}
        component={ResultPayment}
        contextProvider={RewardFormContextProvider}
      />
      <ContextRoute
        exact
        path={routeUrlProvider.getForRoute(QR_RESULT_PATH)}
        component={ResultPayment}
        contextProvider={RewardFormContextProvider}
      />
      <ContextRoute
        exact
        path={routeUrlProvider.getForRoute(CAPTURE_LATER_RESULT_PATH)}
        component={CaptureResult}
        contextProvider={RewardFormContextProvider}
      />

      <Route
        exact
        path={routeUrlProvider.getForRoute(ORACLE_PAYMENT_DETAIL_PATH)}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <OraclePaymentDetail />
          </Suspense>
        )}
      />
      <Route
        exact
        path={routeUrlProvider.getForRoute(ORACLE_PAYMENT_PATH)}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <OraclePayment />
          </Suspense>
        )}
      />
      <Route
        exact
        path={routeUrlProvider.getForRoute(RECEIPT_PATH)}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <Receipt {...props} />
          </Suspense>
        )}
      />
      <Route
        exact
        path={routeUrlProvider.getForRoute(RECEIPT_WITH_TOKEN_PATH)}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <Receipt {...props} />
          </Suspense>
        )}
      />
      <Route
        path={routeUrlProvider.getForRoute(AMOUNT_LESS_PAYMENT_PATH)}
        component={AmountLessPayment}
      />
      <Route
        path={routeUrlProvider.getForRoute(QR_PAYMENT_PATH)}
        render={(props) => {
          props.paymentServiceRepository = paymentServiceRepository;
          return (
            <Suspense fallback={<FullPageSpinner />}>
              <QrPayment {...props} />
            </Suspense>
          );
        }}
      />
      <Route
        path={routeUrlProvider.getForRoute(SEND_QR_RECEIPT_PATH)}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <SendReceipt {...props} />
          </Suspense>
        )}
      />

      <Route
        exact
        path={routeUrlProvider.getForRoute(ERROR_HAS_BEEN_PAID_PATH)}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <ErrorHasBeenPaid {...props?.location?.state} />
          </Suspense>
        )}
      />

      <Route
        exact
        path={routeUrlProvider.getForRoute(ERROR_FULL_ACCESS_PATH)}
        render={() => (
          <Suspense fallback={<FullPageSpinner />}>
            <ErrorFullAccess />
          </Suspense>
        )}
      />

      <Route
        exact
        path={routeUrlProvider.getForRoute(VERIFY_YOUR_PAYMENT_PATH)}
        render={() => (
          <Suspense fallback={<FullPageSpinner />}>
            <PaymentEmailVerify />
          </Suspense>
        )}
      />

      <Route
        exact
        path={routeUrlProvider.getForRoute(LINK_EXPIRED_PATH)}
        render={() => (
          <Suspense fallback={<FullPageSpinner />}>
            <LinkHasExpired />
          </Suspense>
        )}
      />

      <Route
        exact
        path={routeUrlProvider.getForRoute(PAYMENT_STATUS_PATH)}
        render={() => (
          <Suspense fallback={<FullPageSpinner />}>
            <PaymentStatus />
          </Suspense>
        )}
      />
      <Route
        exact
        path={routeUrlProvider.getForRoute(SOMETHING_WRONG_PATH)}
        render={() => (
          <Suspense fallback={<FullPageSpinner />}>
            <ErrorSomethingWrong />
          </Suspense>
        )}
      />

      <Route
        path="/404"
        render={() => (
          <Suspense fallback={<FullPageSpinner />}>
            <FullPageErrorFallback
              error={{
                name: '404',
                message: 'Sorry, the page not found.',
              }}
            />
          </Suspense>
        )}
      />

      <Route
        exact
        path={routeUrlProvider.getForRoute(SHOP_SUCCESS_PATH)}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <ShopSuccess {...props} />
          </Suspense>
        )}
      />
      <Route
        path={routeUrlProvider.getForRoute(WIDGET_DESIGNER_PATH)}
        render={() => (
          <Suspense fallback={<FullPageSpinner />}>
            <WidgetDesigner />
          </Suspense>
        )}
      />
      <Route
        exact
        path={routeUrlProvider.getForRoute(SHOP_PATH)}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <Shop {...props} />
          </Suspense>
        )}
      />

      <Route
        path={routeUrlProvider.getForRoute(PAYMENT_PATH)}
        render={(props) => (
          <Suspense fallback={<FullPageSpinner />}>
            <Payments {...props} />
          </Suspense>
        )}
      />

      <Route
        render={() => (
          <Suspense fallback={<FullPageSpinner />}>
            <FullPageErrorFallback
              error={{
                name: '404',
                message: 'Sorry, the page not found.',
              }}
            />
          </Suspense>
        )}
      />
    </Switch>
  );
};

export default withRouter(Router);
